import React from 'react';
import PropTypes from 'prop-types';
import { Activity, TrendingUp, BarChart3, Star, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import useAnalytics from '@/hooks/useAnalytics';
import { 
  StatCard, 
  AnalyticsChart, 
  TopPagesTable, 
  DateRangePicker 
} from '@/components/analytics';

/**
 * AnalyticsView - Main analytics dashboard component
 * Composes atomic components and uses custom hook for data
 */
const AnalyticsView = () => {
  const {
    analyticsData,
    topPages,
    loading,
    error,
    dateRange,
    setDateRange
  } = useAnalytics();

  // Loading State
  if (loading && !analyticsData) {
    return (
      <div className="space-y-6">
        <DateRangePicker 
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          title="Visitor Analytics"
        />
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a8a] mx-auto"></div>
          <p className="text-slate-600 mt-4">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error && !analyticsData) {
    return (
      <div className="space-y-6">
        <DateRangePicker 
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          title="Visitor Analytics"
        />
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <BarChart3 className="h-16 w-16 mx-auto text-red-300 mb-4" />
            <p className="text-red-600 font-medium">Error loading analytics</p>
            <p className="text-sm text-slate-500 mt-2">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Empty State
  if (!analyticsData) {
    return (
      <div className="space-y-6">
        <DateRangePicker 
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          title="Visitor Analytics"
        />
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <BarChart3 className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">No analytics data available yet.</p>
            <p className="text-sm text-slate-500 mt-2">
              Visitor tracking will start collecting data automatically.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Picker */}
      <DateRangePicker 
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        title="Visitor Analytics"
      />

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Today"
          value={analyticsData.today.total_page_views}
          subtitle={
            <span>
              <Users className="inline h-3 w-3 mr-1" />
              {analyticsData.today.unique_visitors} unique visitors
            </span>
          }
          icon={Activity}
          colorScheme="blue"
        />
        
        <StatCard
          title="This Week"
          value={analyticsData.this_week.total_page_views}
          subtitle={
            <span>
              <Users className="inline h-3 w-3 mr-1" />
              {analyticsData.this_week.unique_visitors} unique visitors
            </span>
          }
          icon={TrendingUp}
          colorScheme="green"
        />
        
        <StatCard
          title="This Month"
          value={analyticsData.this_month.total_page_views}
          subtitle={
            <span>
              <Users className="inline h-3 w-3 mr-1" />
              {analyticsData.this_month.unique_visitors} unique visitors
            </span>
          }
          icon={BarChart3}
          colorScheme="purple"
        />
        
        <StatCard
          title="This Year"
          value={analyticsData.this_year.total_page_views}
          subtitle={
            <span>
              <Users className="inline h-3 w-3 mr-1" />
              {analyticsData.this_year.unique_visitors} unique visitors
            </span>
          }
          icon={Star}
          colorScheme="amber"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnalyticsChart
          title="Visitor Trends"
          data={analyticsData.daily_data}
          type="area"
          dataKeys={['page_views', 'unique_visitors']}
        />
        
        <AnalyticsChart
          title="Daily Comparison"
          data={analyticsData.daily_data.slice(-14)}
          type="bar"
          dataKeys={['page_views', 'unique_visitors']}
        />
      </div>

      {/* Top Pages Table */}
      <TopPagesTable data={topPages} />
      
      {/* Loading overlay when refetching */}
      {loading && (
        <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg px-4 py-2 flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1e3a8a]"></div>
          <span className="text-sm text-slate-600">Updating...</span>
        </div>
      )}
    </div>
  );
};

AnalyticsView.propTypes = {};

export default AnalyticsView;
